import { PlaceSuggestion } from '@components/LocationAutocomplete'
import type { GetLocationType } from '@orbit/api-types'

/**
 * Wire-contract bound on a location's text, mirrored by name from
 * `@orbit/api-types` because that package is not browser-safe
 * at runtime; pinned by `__tests__/contract.test.ts`. The server rejects a
 * blank or longer fullText, so the flow refuses it first.
 */
export const MAX_LOCATION_FULL_TEXT_LENGTH = 255

/** Why a typed place cannot be a destination. */
export type PlaceTextProblem = 'blank' | 'tooLong'

/** The contract's own rule on a place's text, or null when the text passes. */
export const placeTextProblem = (text: string): PlaceTextProblem | null => {
	const trimmed = text.trim()
	if (trimmed.length === 0) return 'blank'
	if (trimmed.length > MAX_LOCATION_FULL_TEXT_LENGTH) return 'tooLong'
	return null
}

/**
 * A destination candidate in the create-trip flow: what the row shows (name +
 * kind label) plus the location payload the create request will send — the same
 * shape every other create path (events, ideas, accommodations) sends, so a
 * fresh place becomes a first-class Location record. Free text is a location
 * with no google identity.
 */
export interface DraftPlace {
	/** Row title — the picked place's primary text, or the free text itself. */
	name: string
	/** Display-only sub-line: "City · Japan", "Campsite · Yosemite, CA", or the kind word alone. */
	kindLabel: string
	location: {
		fullText: string
		primaryText?: string
		secondaryText?: string
		googlePlaceId?: string
		googleTypes: string[]
		locality?: string
	}
}

/**
 * Coarse kind words matched against Google place types, most specific first —
 * so a national park (also typed "establishment") reads "Park" and a campground
 * (also "lodging") reads "Campsite". Mirrors the iOS create-trip flow's list.
 */
const KINDS_BY_PRIORITY: Array<[Set<string>, string]> = [
	[new Set(['country']), 'Country'],
	[
		new Set([
			'administrative_area_level_1',
			'administrative_area_level_2',
			'colloquial_area',
			'archipelago',
			'continent',
		]),
		'Region',
	],
	[new Set(['locality', 'postal_town']), 'City'],
	[
		new Set(['sublocality', 'sublocality_level_1', 'sublocality_level_2', 'neighborhood']),
		'Neighbourhood',
	],
	[new Set(['national_park', 'state_park', 'park']), 'Park'],
	[new Set(['campground', 'camping_cabin', 'rv_park']), 'Campsite'],
	[new Set(['natural_feature', 'beach']), 'Nature'],
	[
		new Set([
			'lodging',
			'hotel',
			'resort_hotel',
			'hostel',
			'motel',
			'bed_and_breakfast',
			'guest_house',
			'inn',
		]),
		'Hotel',
	],
]

const kindWord = (types: string[]): string => {
	for (const [matching, word] of KINDS_BY_PRIORITY) {
		if (types.some(type => matching.has(type))) return word
	}
	return 'Place'
}

const kindLabel = (word: string, detail: string): string =>
	detail ? `${word} · ${detail}` : word

/**
 * A destination candidate from a Google autocomplete pick. A description
 * longer than the contract allows (rare, but Google does not promise a bound)
 * is clamped rather than refused — the place is still the one picked.
 */
export const placeFromSuggestion = (suggestion: PlaceSuggestion): DraftPlace => {
	const name = suggestion.primaryText.trim() || suggestion.fullText.trim()
	const detail = suggestion.secondaryText.trim()
	return {
		name,
		kindLabel: kindLabel(kindWord(suggestion.types), detail),
		location: {
			fullText: suggestion.fullText.trim().slice(0, MAX_LOCATION_FULL_TEXT_LENGTH),
			primaryText: suggestion.primaryText,
			...(detail ? { secondaryText: suggestion.secondaryText } : {}),
			googlePlaceId: suggestion.placeId,
			googleTypes: suggestion.types,
		},
	}
}

/**
 * A destination candidate rebuilt from a saved location — how the edit flow
 * seeds a trip's destinations. The location payload carries everything the
 * record holds, so an unchanged row resolves back to the same place.
 */
export const placeFromLocation = (location: GetLocationType): DraftPlace => {
	const primary = location.primaryText?.trim() ?? ''
	const name = primary || location.fullText.trim()
	const detail = location.secondaryText?.trim() ?? ''
	return {
		name,
		kindLabel: kindLabel(kindWord(location.googleTypes), detail),
		location: {
			fullText: location.fullText,
			...(primary ? { primaryText: primary } : {}),
			...(detail ? { secondaryText: detail } : {}),
			...(location.googlePlaceId ? { googlePlaceId: location.googlePlaceId } : {}),
			googleTypes: location.googleTypes,
			...(location.locality ? { locality: location.locality } : {}),
		},
	}
}

/** A destination candidate typed as free text — anywhere works. */
export const placeFromFreeText = (text: string): DraftPlace => {
	const name = text.trim()
	return {
		name,
		kindLabel: 'Place',
		location: {
			fullText: name,
			primaryText: name,
			googleTypes: [],
		},
	}
}
